import { useRef } from "react";
import {
  ImagePlus,
  Square,
  Circle,
  Minus,
  Type,
  Trash2,
  Lock,
  Unlock,
  Palette,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import type {
  MapOverlay,
  MapOverlayUpdate,
  OverlayType,
} from "@/types/camera-plans";

interface MapOverlayToolbarProps {
  cameraPlanId: string;
  onAddShape: (type: OverlayType) => void;
  onUploadImage: (file: File) => void;
  selectedOverlay: MapOverlay | null;
  onUpdateOverlay: (updates: MapOverlayUpdate) => void;
  onDeleteOverlay: () => void;
}

export function MapOverlayToolbar({
  onAddShape,
  onUploadImage,
  selectedOverlay,
  onUpdateOverlay,
  onDeleteOverlay,
}: MapOverlayToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadImage(file);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Add shape tools */}
      <div className="flex items-center gap-0.5 rounded-md border p-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Add image"
          onClick={handleImageUpload}
        >
          <ImagePlus className="h-3.5 w-3.5" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Add rectangle"
          onClick={() => onAddShape("rectangle")}
        >
          <Square className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Add ellipse"
          onClick={() => onAddShape("ellipse")}
        >
          <Circle className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Add line"
          onClick={() => onAddShape("line")}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Add text"
          onClick={() => onAddShape("text")}
        >
          <Type className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Properties of selected overlay */}
      {selectedOverlay && (
        <>
          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Color */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2">
                <div
                  className="w-3 h-3 rounded-sm border"
                  style={{ backgroundColor: selectedOverlay.color }}
                />
                <Palette className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 space-y-3" align="start">
              <div>
                <Label className="text-xs">Color</Label>
                <Input
                  type="color"
                  value={selectedOverlay.color}
                  onChange={(e) => onUpdateOverlay({ color: e.target.value })}
                  className="h-8 w-full"
                />
              </div>
              <div>
                <Label className="text-xs">
                  Opacity: {Math.round(selectedOverlay.opacity * 100)}%
                </Label>
                <Slider
                  value={[selectedOverlay.opacity]}
                  onValueChange={([v]) => onUpdateOverlay({ opacity: v })}
                  min={0.05}
                  max={1}
                  step={0.05}
                />
              </div>
              <div>
                <Label className="text-xs">Stroke width</Label>
                <Slider
                  value={[selectedOverlay.strokeWidth]}
                  onValueChange={([v]) => onUpdateOverlay({ strokeWidth: v })}
                  min={0}
                  max={10}
                  step={0.5}
                />
              </div>
              {(selectedOverlay.overlayType === "rectangle" ||
                selectedOverlay.overlayType === "ellipse") && (
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={selectedOverlay.fill}
                    onChange={(e) =>
                      onUpdateOverlay({ fill: e.target.checked })
                    }
                  />
                  Fill shape
                </label>
              )}
            </PopoverContent>
          </Popover>

          {/* Text label editing */}
          {selectedOverlay.overlayType === "text" && (
            <Input
              value={selectedOverlay.label || ""}
              onChange={(e) => onUpdateOverlay({ label: e.target.value })}
              className="h-7 w-32 text-sm"
              placeholder="Label text"
            />
          )}

          {/* Lock */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={selectedOverlay.locked ? "Unlock" : "Lock"}
            onClick={() => onUpdateOverlay({ locked: !selectedOverlay.locked })}
          >
            {selectedOverlay.locked ? (
              <Lock className="h-3.5 w-3.5" />
            ) : (
              <Unlock className="h-3.5 w-3.5" />
            )}
          </Button>

          {/* Delete */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            title="Delete overlay"
            onClick={onDeleteOverlay}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
